/**
 * Footer (V2, Phase 11.5.7 — literal realDemo clone). ONE component per kind, composed
 * from the Production Foundation and ported 1:1 from the reference `footer-landing/`.
 * Reads `section.layout` and applies the matching CSS layout (footer.css). The DOM mirrors
 * the reference footer EXACTLY (for the ported parts), with `.sbp-footer*` class names in
 * place of the reference `.landing-footer*`.
 *
 * The reference footer is heavily app-coupled (`useInitInfo`/router/shopping/navigation
 * contexts, app-routed pages, app-store badges, payments, "maroof"/CR/tax, i18n). Per the
 * phase rule ONLY the chrome Builder V2 has data for is ported:
 *   - brand name        (content.heading)            → LandingFooterName (title only, no tagline)
 *   - link column        (content.items[])            → LandingFooterPages  (title "Pages")
 *   - social links       (content.social[] URLs)      → LandingFooterSocial (title "Social", platform icons)
 *   - contact column      (content.contact/location)   → LandingFooterContacts (title "Contact")
 *   - copyright          (year + brand)               → LandingFooterPolicyPowerby
 * EXCLUDED (app-coupled, no builder data): order-now button (router.push), footer Apps/
 * Payments fetchers, Terms/Return/Shipping policy links, powered-by, and the entire
 * Maroof/CR/tax block. See footer.css + the phase report for the full mapping.
 *
 * Public API unchanged (SectionComponentProps<'footer'>) — Registry/Renderer/Engine
 * untouched. Section/Container reused from the foundation (Section applies the RuntimeTheme
 * via `themeVars`, `.sbp-section` token scope, `data-ds`, and RTL `dir` for locale 'ar').
 */
import type { SectionComponentProps } from '../../runtime/types'
import { Section, Container, Icon, cx } from '../shared'
import type { IconName } from '../shared'
import './footer.css'

/**
 * Social platform registry — the URL host determines the platform (no manual selection, no
 * hardcoded ordering: the Footer renders whatever valid platforms exist, in schema order).
 * Extensible: add a row to support a new platform.
 */
const SOCIAL_PLATFORMS: { icon: IconName; label: string; test: (host: string) => boolean }[] = [
  { icon: 'facebook', label: 'Facebook', test: (h) => h.includes('facebook.') || h.includes('fb.com') },
  { icon: 'instagram', label: 'Instagram', test: (h) => h.includes('instagram.') },
  { icon: 'twitter', label: 'X', test: (h) => h.includes('twitter.') || h === 'x.com' || h.endsWith('.x.com') },
  { icon: 'linkedin', label: 'LinkedIn', test: (h) => h.includes('linkedin.') },
  { icon: 'tiktok', label: 'TikTok', test: (h) => h.includes('tiktok.') },
  { icon: 'youtube', label: 'YouTube', test: (h) => h.includes('youtube.') || h.includes('youtu.be') },
]

/** Resolve a social URL to its platform icon + label, or null if unrecognized. */
function detectSocial(url: string): { icon: IconName; label: string } | null {
  let host: string
  try {
    host = new URL(url).hostname.replace(/^www\./, '').toLowerCase()
  } catch {
    host = url.toLowerCase()
  }
  const match = SOCIAL_PLATFORMS.find((p) => p.test(host))
  return match ? { icon: match.icon, label: match.label } : null
}

export function Footer(p: SectionComponentProps<'footer'>) {
  const { content, layout, id, theme, locale } = p

  const brand = content.heading ?? ''
  const links = content.items ?? []
  // Only recognized platforms render (URL-detected); unknown/empty entries are dropped.
  const socialItems = (content.social ?? [])
    .map((url) => ({ url, meta: detectSocial(url) }))
    .filter((s): s is { url: string; meta: { icon: IconName; label: string } } => s.meta !== null)

  // Contact rows — reference LandingFooterContacts (phone → tel:, email → mailto:, address
  // → no link). Sourced from content.contact + content.location; each present field is a row.
  const phone = content.contact?.phone?.trim()
  const email = content.contact?.email?.trim()
  const address = content.location?.address?.trim()
  const contactRows = [
    phone ? { key: 'phone', value: phone, href: `tel:${phone}` } : null,
    email ? { key: 'email', value: email, href: `mailto:${email}` } : null,
    address ? { key: 'address', value: address, href: '' } : null,
  ].filter((r): r is { key: string; value: string; href: string } => r !== null)

  const year = new Date().getFullYear()

  return (
    <Section
      as="footer"
      theme={theme}
      locale={locale}
      id={id}
      className={cx('sbp-footer', `sbp-footer--${layout}`)}
    >
      {/* reference .landing-footer-container */}
      <Container className="sbp-footer__container">
        {/* LandingFooterName — title only (reference name block has no tagline) */}
        <section className="sbp-footer__name">
          {brand ? <p className="sbp-footer__name-title">{brand}</p> : null}
        </section>

        {/* LineSeparatorHorizontal — rendered unconditionally, as the reference does */}
        <div className="sbp-footer__sep" />

        {/* LandingFooterSections */}
        <div className="sbp-footer__sections">
          {/* LandingFooterSocial — platform icons (no hostname text); URL → platform */}
          {socialItems.length > 0 ? (
            <section className="sbp-footer__section">
              <p>Social</p>
              <ul className="sbp-footer__social">
                {socialItems.map(({ url, meta }) => (
                  <li key={url}>
                    <a
                      className="sbp-footer__social-link"
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={meta.label}
                    >
                      <Icon name={meta.icon} title={meta.label} />
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* LandingFooterPages */}
          {links.length > 0 ? (
            <section className="sbp-footer__section">
              <p>Pages</p>
              <ul>
                {links.map((link) => (
                  <li key={link.id}>
                    <a className="sbp-footer__link" href={link.value ?? '#'}>
                      {link.title}
                    </a>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}

          {/* LandingFooterContacts */}
          {contactRows.length > 0 ? (
            <section className="sbp-footer__section">
              <p>Contact</p>
              <ul>
                {contactRows.map((row) =>
                  row.href ? (
                    <li key={row.key}>
                      <a className="sbp-footer__link" href={row.href}>
                        {row.value}
                      </a>
                    </li>
                  ) : (
                    <li key={row.key} className="disable">
                      <span>{row.value}</span>
                    </li>
                  ),
                )}
              </ul>
            </section>
          ) : null}
        </div>

        {/* LineSeparatorHorizontal — rendered unconditionally, as the reference does */}
        <div className="sbp-footer__sep" />

        {/* LandingFooterPolicyPowerby */}
        <section className="sbp-footer__policy-powerby">
          <div className="sbp-footer__policy">
            <span>
              © {year}
              {brand ? ` ${brand}` : ''}
            </span>
          </div>
        </section>
      </Container>
    </Section>
  )
}
